/**
 * Support Tab Component
 * 
 * User-facing support ticket management interface.
 * Allows users to create tickets, view their tickets, and add messages.
 */

import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import {
  createTicket,
  listUserTickets,
  getTicketDetails,
  addMessageToTicket,
  type TicketResponse,
  type TicketDetailResponse,
  type CreateTicketRequest,
} from "@app/services/supportApi";
import "./support.css";

const SUPPORT_QUERY_KEY = ["support", "tickets"];

export function SupportTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateTicketRequest>({
    subject: "",
    message: "",
    priority: "normal",
    reference_id: "",
    reference_type: "",
  });
  const [messageText, setMessageText] = useState("");
  const [notice, setNotice] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const { data: tickets, isLoading } = useQuery({
    queryKey: SUPPORT_QUERY_KEY,
    queryFn: () => listUserTickets(),
  });

  const { data: ticketDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: [...SUPPORT_QUERY_KEY, selectedTicketId],
    queryFn: () => {
      if (!selectedTicketId) {
        return null;
      }
      return getTicketDetails(selectedTicketId);
    },
    enabled: !!selectedTicketId,
  });

  const createMutation = useMutation({
    mutationFn: createTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEY });
      setNotice({ type: "success", message: "Ticket created successfully." });
      setIsCreateFormOpen(false);
      setFormData({
        subject: "",
        message: "",
        priority: "normal",
        reference_id: "",
        reference_type: "",
      });
    },
    onError: (error: unknown) => {
      setNotice({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to create ticket.",
      });
    },
  });

  const addMessageMutation = useMutation({
    mutationFn: ({ ticketId, message }: { ticketId: string; message: string }) =>
      addMessageToTicket(ticketId, { message }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEY });
      if (selectedTicketId) {
        queryClient.invalidateQueries({ queryKey: [...SUPPORT_QUERY_KEY, selectedTicketId] });
      }
      setMessageText("");
      setNotice({ type: "success", message: "Message added successfully." });
    },
    onError: (error: unknown) => {
      setNotice({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to add message.",
      });
    },
  });

  useEffect(() => {
    if (notice) {
      const timer = setTimeout(() => setNotice(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notice]);

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.subject || !formData.message) {
      setNotice({ type: "error", message: "Subject and message are required." });
      return;
    }
    createMutation.mutate({
      ...formData,
      reference_id: formData.reference_id || undefined,
      reference_type: formData.reference_type || undefined,
    });
  };

  const handleAddMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicketId || !messageText.trim()) {
      setNotice({ type: "error", message: "Message cannot be empty." });
      return;
    }
    addMessageMutation.mutate({ ticketId: selectedTicketId, message: messageText });
  };

  const getStatusPill = (status: string) => {
    const tone: "success" | "warning" | "danger" | "neutral" =
      status === "resolved" || status === "closed"
        ? "success"
        : status === "in_progress"
          ? "warning"
          : status === "open"
            ? "neutral"
            : "danger";
    return <Pill tone={tone}>{status}</Pill>;
  };

  const getPriorityPill = (priority: string) => {
    const tone: "success" | "warning" | "danger" | "neutral" =
      priority === "urgent"
        ? "danger"
        : priority === "high"
          ? "warning"
          : priority === "low"
            ? "success"
            : "neutral";
    return <Pill tone={tone}>{priority}</Pill>;
  };

  return (
    <div className="support-root">
      {notice ? (
        <div className={`support-banner support-banner--${notice.type}`}>{notice.message}</div>
      ) : null}

      <Card
        title="Support Tickets"
        subtitle="Create a ticket to get help with trading issues, API problems, or account questions."
      >
        <div className="support-actions">
          <Button variant="primary" onClick={() => setIsCreateFormOpen(true)}>
            Create New Ticket
          </Button>
        </div>

        {isCreateFormOpen && (
          <form className="support-form" onSubmit={handleCreateSubmit}>
            <h3>Create Support Ticket</h3>
            <label>
              Subject *
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                maxLength={255}
              />
            </label>
            <label>
              Priority
              <select
                value={formData.priority}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    priority: e.target.value as "low" | "normal" | "high" | "urgent",
                  })
                }
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <label>
              Reference ID (optional)
              <input
                type="text"
                value={formData.reference_id}
                onChange={(e) => setFormData({ ...formData, reference_id: e.target.value })}
                placeholder="Execution Job ID, Payment Intent ID, etc."
                maxLength={64}
              />
            </label>
            <label>
              Reference Type (optional)
              <input
                type="text"
                value={formData.reference_type}
                onChange={(e) => setFormData({ ...formData, reference_type: e.target.value })}
                placeholder="execution_job, payment_intent, etc."
                maxLength={32}
              />
            </label>
            <label>
              Message *
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={6}
                placeholder="Describe your issue in detail..."
              />
            </label>
            <div className="support-form-actions">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsCreateFormOpen(false);
                  setFormData({
                    subject: "",
                    message: "",
                    priority: "normal",
                    reference_id: "",
                    reference_type: "",
                  });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Ticket"}
              </Button>
            </div>
          </form>
        )}

        {isLoading ? (
          <div className="support-empty">Loading tickets...</div>
        ) : !tickets || tickets.length === 0 ? (
          <div className="support-empty">No tickets yet. Create your first ticket to get help.</div>
        ) : (
          <div className="support-tickets-list">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className={`support-ticket-item ${
                  selectedTicketId === ticket.id ? "support-ticket-item--active" : ""
                }`}
                onClick={() => setSelectedTicketId(ticket.id)}
              >
                <div className="support-ticket-item__header">
                  <h4>{ticket.subject}</h4>
                  <div className="support-ticket-item__badges">
                    {getStatusPill(ticket.status)}
                    {getPriorityPill(ticket.priority)}
                  </div>
                </div>
                <div className="support-ticket-item__meta">
                  <span>Created: {new Date(ticket.created_at).toLocaleString()}</span>
                  {ticket.reference_id && (
                    <span>Reference: {ticket.reference_id}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {selectedTicketId && ticketDetails && (
        <Card title={`Ticket: ${ticketDetails.ticket.subject}`}>
          <div className="support-ticket-details">
            <div className="support-ticket-details__header">
              <div className="support-ticket-details__meta">
                <span>Status: {getStatusPill(ticketDetails.ticket.status)}</span>
                <span>Priority: {getPriorityPill(ticketDetails.ticket.priority)}</span>
                <span>Created: {new Date(ticketDetails.ticket.created_at).toLocaleString()}</span>
                {ticketDetails.ticket.resolved_at && (
                  <span>Resolved: {new Date(ticketDetails.ticket.resolved_at).toLocaleString()}</span>
                )}
              </div>
            </div>

            <div className="support-messages">
              <h4>Messages</h4>
              {ticketDetails.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`support-message ${
                    msg.is_from_admin ? "support-message--admin" : "support-message--user"
                  }`}
                >
                  <div className="support-message__header">
                    <span className="support-message__author">
                      {msg.is_from_admin ? "Admin" : "You"}
                    </span>
                    <span className="support-message__time">
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="support-message__content">{msg.message}</div>
                </div>
              ))}
            </div>

            {ticketDetails.ticket.status !== "resolved" && ticketDetails.ticket.status !== "closed" && (
              <form className="support-add-message-form" onSubmit={handleAddMessage}>
                <label>
                  Add Message
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows={4}
                    placeholder="Type your message..."
                    required
                  />
                </label>
                <Button type="submit" variant="primary" disabled={addMessageMutation.isPending}>
                  {addMessageMutation.isPending ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

